import {useEffect, useRef} from "react";
import {useLocation} from "react-router-dom";
import {useSiteNavigate} from "nav/SiteLink";
import {useLocale, useMessages} from "i18n";
import {AuthenticatedSessionState} from "auth/AuthenticatedSessionWaiter";
import Seo from "../seo/Seo";
import {siteOrigin} from "../seo/siteMeta";
import {pricingJsonLd, pricingOffersForContext} from "../seo/structuredData";
import PricingPage from "../pricing/PricingPage";
import {usePricingUpgrade} from "../pricing/usePricingUpgrade";
import {parseUpgradeIntent, withoutUpgradeIntent} from "../pricing/upgradeIntent";
import {UpgradeCheckoutDialog} from "../pay/UpgradeCheckoutDialog";
import {PlanChangeDialog} from "../pricing/PlanChangeDialog";
import {useAuthenticatedSession, useIsLoggedIn, useUser} from "../UserProvider";
import {planOf} from "../home/gating";
import {useMountedHostname} from "../utils/useMountedHostname";
import {useLadderOffer} from "../offers/ladder/useLadderOffer";
import {parseLimitWallToken} from "../offers/limitWall/limitWallOffer";
import {usePricingPage} from "../pricing/usePricingPage";

/**
 * Public, pre-rendered pricing page (restored with the v3 nav — the route was dropped in the
 * Vite migration while the page component survived). Card CTAs go through the shared
 * {@link usePricingUpgrade} action (login modal when anonymous, `/account` when signed-in) —
 * the same seam the landing's pricing dialog uses (ticket #45).
 *
 * Structured-data prices come from the amounts baked from live Stripe at build time ({@link
 * pricingOffersForContext}, ui#116) — the same baked amounts the cards paint, resolved through the same
 * locale+host context — so the quoted MONEY is amount-accurate and is never a currency this page's own
 * checkout would refuse to charge. All four offers follow canonical catalogue order and are emitted at
 * SSG time; there is no runtime `/plan` fetch, so the cards render these same baked amounts (ui#241).
 * Free is 0. Tier names follow the active locale (ui#101).
 *
 * The entity's `url` follows the HOST too, from the same `canonicalLocale` `<Seo>` canonicalizes on
 * (#1135) — so the offers and the URL they are attributed to always name one storefront. Only the tier
 * NAMES follow the reader's language: they are copy, not identity.
 *
 * `/pricing?offer=<token>` is where a lifecycle offer ladder link lands (kitecorp/transferify-ui#1977):
 * the api records the click and redirects here carrying the token, {@link useLadderOffer} resolves what
 * it is worth, and the page states the rung's offer or the expired notice. The token then rides on the
 * checkout request, which is what makes the api apply the rung's discount and attribute the sale — but
 * only the token {@link usePricingPage} DECIDED (`ladderOfferToken`), never the link's raw resolution,
 * so the page and the till cannot state two different offers.
 *
 * `/pricing?wall=<token>` was where the limit-wall coupon email landed (kitecorp/transferify-api-service#2206)
 * until kitecorp/transferify-ui#3197 moved it to the send screen, `/?wall=<token>`, where the sender can
 * finish the blocked transfer. The api's redirect goes there now; a direct visit here with the parameter
 * (an old bookmark, a copied address) is forwarded there with its whole query, so the link still works.
 *
 * `/pricing?upgrade=<plan>` presses that plan's card on arrival (kitecorp/transferify-api-service#1192):
 * the api's download-limit notice links here so an emailed sender lands in the existing checkout in one
 * click. The press waits for the auth session to settle, because {@link usePricingUpgrade} decides
 * login-versus-checkout from the account on hand, and on the first client render a returning visitor's
 * session is still being restored, which would send a signed-in sender through the login modal. It
 * carries `settledLadderOfferToken` rather than a token read here, because an anonymous reader's press
 * is resumed after the login modal and the rung's fate is decided by a welcome window this render has
 * not heard about yet.
 */
export function Component() {
	const {canonicalLocale, storefrontLocale} = useLocale();
	const {pricing, seo} = useMessages();
	const [user] = useUser();
	const [session] = useAuthenticatedSession();
	const location = useLocation();
	const navigate = useSiteNavigate();
	const {onUpgrade, checkout, closeCheckout, planChange} = usePricingUpgrade("EMAIL_UPGRADE_LINK");
	const ladder = useLadderOffer();
	// Forwarded THROUGH the router with `replace`, so Back does not return to a page that forwards again.
	useEffect(() => {
		if (parseLimitWallToken(location.search) === null) {
			return;
		}
		navigate({pathname: "/", search: location.search, hash: location.hash}, {replace: true});
	}, [navigate, location.search, location.hash]);
	// Hydration-safe (ui#74/#202): `/pricing` is SSG-pre-rendered as anonymous, but UserProvider seeds
	// from localStorage on the first client render — so reading `user.isLoggedIn` directly here would
	// paint the "Current plan"/"Upgrade" CTAs on the first render for a returning user and diverge from
	// the logged-out server HTML (React hydration mismatch, #418). `useIsLoggedIn` is `false` on the
	// server AND the first client render, so `currentPlanId` stays `undefined` (matching the anonymous
	// SSG) and the plan-aware CTAs swap in only after mount via a normal re-render.
	const isLoggedIn = useIsLoggedIn();
	const currentPlanId = isLoggedIn && !user.isEnterprise() ? planOf(user) : undefined;

	// The page's view-model is built HERE rather than inside `PricingPage`, because the checkout dialog
	// below is the route's and has to send the same answers the page renders from. One call, one welcome
	// read: a second one would ask the counter twice and report a second showing for one visit.
	const page = usePricingPage(currentPlanId, true, false, ladder);
	// Sent only while a discount-bearing rung is live AND the welcome price is not (`ladderBeside`, the
	// owner's "welcome wins over the ladder"). An expired token on a checkout is refused outright, which
	// would turn a reader who accepted list prices into one who cannot buy at all; a set-aside rung's
	// token would charge a discount this page has stopped showing.
	const offerToken = page.ladderOfferToken;
	const settledLadderOfferToken = page.settledLadderOfferToken;

	// Consumed ONCE per page load, and only after the session stopped being pending. Held in a ref rather
	// than state: a second run of the effect (Strict Mode, a session flip, the strip below changing the
	// location) must not press the card twice.
	const intentConsumed = useRef(false);
	useEffect(() => {
		if (intentConsumed.current || session === AuthenticatedSessionState.Pending) {
			return;
		}
		const intended = parseUpgradeIntent(location.search);
		if (intended === null) {
			return;
		}
		intentConsumed.current = true;
		// Stripped through the router rather than `history.replaceState`, so `useLocation` readers and the
		// address bar agree; a reload or a copied address then does not re-open checkout.
		navigate({pathname: location.pathname, search: withoutUpgradeIntent(location.search), hash: location.hash},
			{replace: true});
		// The rung is handed over as a RESOLVER, the same form a card press uses, because this press can
		// be resumed after the login modal: an anonymous reader's account — and the welcome window that
		// may retire the rung — arrives only then. A token read here would be the pre-login answer.
		//
		// Nobody pressed a card, so no picker press is recorded and the checkout counts on this route's own
		// door, `EMAIL_UPGRADE_LINK` above (kitecorp/transferify-ui#2914). A card pressed on this page
		// records its own press and counts on the plan picker.
		onUpgrade(intended, undefined, settledLadderOfferToken);
	}, [session, settledLadderOfferToken, onUpgrade, navigate, location.pathname, location.search, location.hash]);

	const hostname = useMountedHostname();
	// The offers denominate what the CARDS show, so they take the storefront locale the cards take
	// (ui#2471) — a structured-data price that is not the page's price is a policy violation, and
	// on `transferify.cloud/ro/pricing` the cards quote the visitor's storefront. The entity `url`
	// below still follows `canonicalLocale`, which is the page's identity rather than its money;
	// `seo/structuredData.pricingOffersForContext` states why the two are allowed to differ.
	const offers = pricingOffersForContext(storefrontLocale, hostname, pricing.planNames);
	const jsonLd = pricingJsonLd(siteOrigin(canonicalLocale), offers.tiers, offers.currencyCode);

	return (
		<>
			<Seo
				title={seo.pricing.title}
				description={seo.pricing.description}
				path="/pricing"
				jsonLd={jsonLd}
			/>
			<PricingPage page={page} onUpgrade={onUpgrade} ladder={ladder}/>
			<UpgradeCheckoutDialog
				checkout={checkout}
				onClose={closeCheckout}
				terms={offerToken === undefined ? undefined : {offerToken}}
				// The api refused the token at the till: nothing was charged, so the dialog closes and the
				// page states it with the same notice a dead link gets, at list prices.
				onLadderOfferExpired={() => {
					ladder.markExpired();
					closeCheckout();
				}}
			/>
			<PlanChangeDialog {...planChange}/>
		</>
	);
}
